class MainProduct extends HTMLElement {
  constructor() {
    super();
    this._mpGuaranteeTimer = null;
    this._onGuaranteeClick = null;
    this._deliveryTimer = null;
  }

  connectedCallback() {
    this.initSlider();
    this.initGuaranteeSlider();
    this.initDeliveryBlocks();
    this.initSizeVariants();
    this.initColorVariants();
    this.initAddToCartBlock();
    this.initImageZoom();
    this.initGalleryModal();

    this.variantBtn = this.querySelectorAll(".button-variant");
    this.variantBtn.forEach((btn) => {
      btn.addEventListener("click", (e) => this.toggleClassActive(e));
    });

    // Last: on mobile it moves blocks out of <main-product> (the collection
    // cards above among them), so everything found with this.querySelector
    // must be wired before.
    this.initMobileLayout();

    this.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-quick-atc]");
      if (!btn) return;
      e.preventDefault();

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      this.quickAddToCart(variantId, 1);
    });
  }

  disconnectedCallback() {
    if (this._mpGuaranteeTimer) {
      clearInterval(this._mpGuaranteeTimer);
      this._mpGuaranteeTimer = null;
    }

    // si on avait attaché un handler spécifique
    const wrap = this.querySelector("[data-mp-guarantee]");
    if (wrap && this._onGuaranteeClick) {
      wrap.removeEventListener("click", this._onGuaranteeClick);
      this._onGuaranteeClick = null;
    }

    if (this._deliveryTimer) {
      clearInterval(this._deliveryTimer);
      this._deliveryTimer = null;
    }

    if (this._onResize) {
      window.removeEventListener("resize", this._onResize);
      this._onResize = null;
    }

    if (this._mobileLayout) {
      this._mobileLayout.destroy();
      this._mobileLayout = null;
    }
  }

  // Mobile layout of a customizable product (see main-product.css): the
  // configurator is a separate section rendered AFTER this one, so the details
  // that must sit under it (description, collapses, other collections) are moved
  // there, and brought back on desktop. The top CTA becomes a bottom bar shown
  // only once the visitor has scrolled past the configurator.
  initMobileLayout() {
    if (!this.classList.contains("main-product--customizable")) return;

    const customizer = document.getElementById("product-customizer");
    if (!customizer) return;
    const anchor = customizer.closest(".shopify-section") || customizer;

    // "first" items (the reviews badge) open the block, the rest keep their order.
    const details = Array.from(this.querySelectorAll("[data-mp-mobile-details]"));
    const ordered = details
      .filter((el) => el.dataset.mpMobileDetails === "first")
      .concat(details.filter((el) => el.dataset.mpMobileDetails !== "first"));
    const placeholders = details.map((el) => {
      const mark = document.createComment("mp-mobile-details");
      el.before(mark);
      return mark;
    });

    const container = document.createElement("div");
    container.className = "mp-mobile-details";

    // Reviews badge under the price: a copy of the one already rendered.
    const topBadge = this.querySelector("[data-mp-reviews-top]");
    const badgeSource = this.querySelector(".mp-reviews-details .reviews__container");

    const mq = window.matchMedia("(max-width: 989px)");
    const apply = () => {
      if (mq.matches) {
        if (topBadge && badgeSource && !topBadge.firstChild) {
          topBadge.appendChild(badgeSource.cloneNode(true));
        }
        ordered.forEach((el) => container.appendChild(el));
        anchor.after(container);
      } else {
        details.forEach((el, i) => placeholders[i].after(el));
        container.remove();
      }
      this.vSwiper?.update();
    };
    mq.addEventListener("change", apply);
    apply();

    const cta = this.querySelector(".cta-personalise");
    let observer = null;
    let onCtaClick = null;
    if (cta && "IntersectionObserver" in window) {
      // Visible only when the configurator is entirely ABOVE the screen.
      observer = new IntersectionObserver(([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        if (scrolledPast) {
          // Theme preview only: Shopify's preview bar is pinned to the bottom of
          // the screen and would cover the button. Sit just above it.
          const previewBar = document.getElementById("PBarNextFrameWrapper");
          const barHeight = previewBar ? Math.round(previewBar.getBoundingClientRect().height) : 0;
          cta.style.setProperty("--mp-cta-offset", `${barHeight}px`);
        }
        cta.classList.toggle("is-sticky-visible", scrolledPast);
      });
      observer.observe(customizer);

      onCtaClick = (e) => {
        if (!mq.matches) return;
        e.preventDefault();
        customizer.scrollIntoView({ behavior: "smooth", block: "start" });
      };
      cta.addEventListener("click", onCtaClick);
    }

    this._mobileLayout = {
      destroy: () => {
        mq.removeEventListener("change", apply);
        observer?.disconnect();
        if (cta && onCtaClick) cta.removeEventListener("click", onCtaClick);
        cta?.classList.remove("is-sticky-visible");
        topBadge?.replaceChildren();
        details.forEach((el, i) => placeholders[i].after(el));
        placeholders.forEach((mark) => mark.remove());
        container.remove();
      },
    };
  }

  initSlider() {
    this.productSwiperEl = this.querySelector(".swiper-product");
    const variantsSwiperEl = this.querySelector(".swiper-variants");

    this._onResize = () => this.handleProductSwiperResize();
    window.addEventListener("resize", this._onResize);
    this.handleProductSwiperResize();

    if (variantsSwiperEl) {
      this.vSwiper = new Swiper(variantsSwiperEl, {
        slidesPerView: "auto",
        spaceBetween: 4,
        scrollbar: {
          el: this.querySelector(".swiper-scrollbar"),
          draggable: true,
          hide: false,
        },
      });
    }
  }

  handleProductSwiperResize() {
    const isMobile = window.innerWidth < 990;

    if (isMobile && !this.swiper && this.productSwiperEl) {
      this.swiper = new Swiper(this.productSwiperEl, {
        slidesPerView: 1.25,
        spaceBetween: 6,
        pagination: {
          el: this.querySelector(".swiper-pagination"),
          clickable: true,
        },
      });
    } else if (!isMobile && this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  initGuaranteeSlider() {
    // reset timer si ré-init
    if (this._mpGuaranteeTimer) {
      clearInterval(this._mpGuaranteeTimer);
      this._mpGuaranteeTimer = null;
    }

    const wrap = this.querySelector("[data-mp-guarantee]");
    if (!wrap) return;

    const textEl = wrap.querySelector(".mp-guarantee__text");
    const pagWrap = wrap.querySelector("[data-mp-guarantee-pagination]");
    const dataScript = wrap.querySelector(".mp-guarantee__data");
    if (!textEl || !pagWrap || !dataScript) return;

    let data;
    try {
      data = JSON.parse(dataScript.textContent || "[]");
    } catch (e) {
      return;
    }
    if (!Array.isArray(data) || data.length === 0) return;

    const delay = parseInt(wrap.dataset.autoplayDelay, 10) || 3000;
    wrap.style.setProperty("--mp-guarantee-delay", `${delay}ms`);

    // construit exactement le bon nombre de paginations
    pagWrap.innerHTML = data
      .map(
        (_, i) => `
        <div class="mp-guarantee__pagination-item" data-index="${i}">
          <div class="mp-guarantee__pagination-bar">
            <div class="mp-guarantee__pagination-fill"></div>
          </div>
          <div class="mp-guarantee__pagination-dot"></div>
        </div>
      `,
      )
      .join("");

    const items = Array.from(
      pagWrap.querySelectorAll(".mp-guarantee__pagination-item"),
    );
    let index = 0;

    const show = (i) => {
      const item = data[i];
      if (!item) return;

      textEl.textContent = item.text || "";

      items.forEach((el, idx) => {
        const fill = el.querySelector(".mp-guarantee__pagination-fill");
        const active = idx === i;

        el.classList.toggle("mp-guarantee__pagination-item--active", active);

        if (!fill) return;
        fill.classList.remove("animating");
        if (active) {
          void fill.offsetWidth; // restart animation
          fill.classList.add("animating");
        }
      });
    };

    const start = () => {
      this._mpGuaranteeTimer = setInterval(() => {
        index = (index + 1) % data.length;
        show(index);
      }, delay);
    };

    // évite d’empiler les listeners si réinit
    if (this._onGuaranteeClick) {
      wrap.removeEventListener("click", this._onGuaranteeClick);
    }

    this._onGuaranteeClick = (e) => {
      const btn = e.target.closest(".mp-guarantee__pagination-item");
      if (!btn) return;

      const i = Number(btn.dataset.index || 0);
      index = ((i % data.length) + data.length) % data.length;

      show(index);

      clearInterval(this._mpGuaranteeTimer);
      start();
    };

    wrap.addEventListener("click", this._onGuaranteeClick);

    show(0);
    start();
  }

  toggleClassActive(e) {
    const btn = e.currentTarget;

    this.variantBtn.forEach((b) => b.classList.remove("was-clicked"));
    btn.classList.add("was-clicked");

    const url = btn.dataset.productUrl;
    if (url) window.location.href = url;
  }

  async initDeliveryBlocks() {
    if (this._deliveryTimer) clearInterval(this._deliveryTimer);

    const blocks = this.querySelectorAll("[data-delivery]");
    if (!blocks.length) return;

    // Try to fetch delivery date from API (source of truth)
    const firstBlock = blocks[0];
    const productId = firstBlock.dataset.productId;
    const isLocal = ["127.0.0.1", "localhost"].includes(window.location.hostname);
    const apiEndpoint = isLocal
      ? firstBlock.dataset.apiEndpointLocal
      : firstBlock.dataset.apiEndpoint;

    let apiData = null;
    if (productId && apiEndpoint) {
      try {
        const res = await fetch(`${apiEndpoint}/api/delivery/estimate/${productId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && (data.delivery_date_long || data.delivery_date)) {
            if (!data.delivery_date_long && data.delivery_date) {
              const d = new Date(data.delivery_date);
              data.delivery_date_long = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
            }
            apiData = data;
          }
        }
      } catch { /* fallback to local calculation */ }
    }

    const tick = () => {
      const now = new Date();

      blocks.forEach((block) => {
        // Countdown: use API cutoff or local cutoff
        const cutoffDeadline = apiData?.cutoff_deadline ? new Date(apiData.cutoff_deadline) : null;
        let effectiveDeadline;
        if (cutoffDeadline && cutoffDeadline > now) {
          effectiveDeadline = cutoffDeadline;
        } else {
          const cutoffHour = apiData?.cutoff_hour || Number(block.dataset.cutoffHour || 14);
          const deadlineToday = new Date(now);
          deadlineToday.setHours(cutoffHour, 0, 0, 0);
          effectiveDeadline = now < deadlineToday
            ? deadlineToday
            : new Date(deadlineToday.getTime() + 86400000);
        }

        const diffMs = Math.max(0, effectiveDeadline - now);
        const totalMin = Math.floor(diffMs / 60000);
        const days = Math.floor(totalMin / 1440);
        const hours = Math.floor((totalMin % 1440) / 60);
        const mins = totalMin % 60;

        const cd = block.querySelector("[data-countdown]");
        if (cd) {
          let text = "dans les ";
          if (days > 0) text += `${days}j et ${hours}h`;
          else if (hours > 0) text += `${hours}h`;
          else text += `${mins}min`;
          cd.textContent = text;
        }

        // Delivery date: use API or fallback to local
        const dateEl = block.querySelector("[data-delivery-date]");
        if (dateEl) {
          if (apiData?.delivery_date_long) {
            dateEl.textContent = apiData.delivery_date_long;
          } else {
            const cutoffHour = Number(block.dataset.cutoffHour || 14);
            const deadlineToday = new Date(now);
            deadlineToday.setHours(cutoffHour, 0, 0, 0);
            const beforeCutoff = now < deadlineToday;
            const productionDaysJson = block.dataset.atelierProductionDays;

            let deliveryDate;
            if (productionDaysJson) {
              const atelierDelay = Number(block.dataset.atelierDeliveryDelay || 3);
              const vacStart = block.dataset.atelierVacationStart || null;
              const vacEnd = block.dataset.atelierVacationEnd || null;
              const result = this.calculateAtelierDelivery(now, productionDaysJson, atelierDelay, cutoffHour, vacStart, vacEnd);
              deliveryDate = result.deliveryDate;
            } else {
              const delayDays = Number(block.dataset.delayDays || 3);
              const base = beforeCutoff ? now : new Date(now.getTime() + 86400000);
              deliveryDate = this.addBusinessDays(base, delayDays);
            }
            dateEl.textContent = this.formatDeliveryDate(deliveryDate);
          }
        }
      });
    };

    tick();
    this._deliveryTimer = setInterval(tick, 60000);
  }

  addBusinessDays(startDate, days) {
    const d = new Date(startDate);
    d.setHours(12, 0, 0, 0);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    if (d.getDay() === 6) d.setDate(d.getDate() + 2);
    if (d.getDay() === 0) d.setDate(d.getDate() + 1);
    return d;
  }

  formatDeliveryDate(date) {
    const weekday = date.toLocaleDateString("fr-FR", { weekday: "long" });
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleDateString("fr-FR", { month: "long" });

    return `${weekday} ${day} ${month}`;
  }

  findNextProductionDay(fromDate, jsProductionDays) {
    const d = new Date(fromDate);
    for (let i = 0; i < 14; i++) {
      d.setDate(d.getDate() + 1);
      if (jsProductionDays.includes(d.getDay())) return new Date(d);
    }
    return null;
  }

  calculateAtelierDelivery(
    now,
    productionDaysJson,
    deliveryDelayDays,
    cutoffHour,
    vacationStart,
    vacationEnd,
  ) {
    const isoDays = JSON.parse(productionDaysJson);
    const jsProductionDays = isoDays.map((d) => d % 7);

    const deadlineToday = new Date(now);
    deadlineToday.setHours(cutoffHour, 0, 0, 0);

    const todayJs = now.getDay();
    const isTodayProduction = jsProductionDays.includes(todayJs);
    const beforeCutoff = now < deadlineToday;

    let shipDate;
    if (isTodayProduction && beforeCutoff) {
      shipDate = new Date(now);
      shipDate.setHours(12, 0, 0, 0);
    } else {
      shipDate = this.findNextProductionDay(now, jsProductionDays);
    }

    if (!shipDate)
      return {
        deliveryDate: this.addBusinessDays(now, deliveryDelayDays),
        shipDate: now,
      };

    if (vacationStart && vacationEnd) {
      const vStart = new Date(vacationStart + "T00:00:00");
      const vEnd = new Date(vacationEnd + "T23:59:59");
      if (shipDate >= vStart && shipDate <= vEnd) {
        shipDate = this.findNextProductionDay(vEnd, jsProductionDays);
        if (!shipDate)
          return {
            deliveryDate: this.addBusinessDays(now, deliveryDelayDays),
            shipDate: now,
          };
      }
    }

    const deliveryDate = this.addBusinessDays(shipDate, deliveryDelayDays);
    return { deliveryDate, shipDate };
  }

  initSizeVariants() {
    const sizeContainer = this.querySelector("[data-size-variants]");
    if (!sizeContainer) return;

    this.sizeVariantBtns = sizeContainer.querySelectorAll(".mp-size-variants__item");
    const activeBtn = sizeContainer.querySelector(".mp-size-variants__item--active");
    this.selectedSize = activeBtn ? activeBtn.dataset.size : null;

    this.sizeVariantBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        this.sizeVariantBtns.forEach((b) => {
          b.classList.remove("mp-size-variants__item--active");
          b.setAttribute("aria-pressed", "false");
        });
        btn.classList.add("mp-size-variants__item--active");
        btn.setAttribute("aria-pressed", "true");
        this.selectedSize = btn.dataset.size;

        const sizeValueEl = this.querySelector("[data-size-value]");
        if (sizeValueEl) sizeValueEl.textContent = this.selectedSize;

        // The price under the title follows the size (the configurator already
        // shows the price of the selected size in its summary).
        const priceEl = this.querySelector(".product__price");
        if (priceEl && btn.dataset.price) priceEl.textContent = btn.dataset.price;

        this.syncVariantFromSizeAndColor();

        // Re-filter colors by stock for the new size
        if (this._stockData) {
          const colorVariantsContainer = this.querySelector("[data-color-variants]");
          if (colorVariantsContainer) this.refilterColorsByStock(colorVariantsContainer);
        }

        // Sync with product-customizer size buttons
        const customizerSizeBtn = document.querySelector(`.product-customizer__size-btn[data-size="${this.selectedSize}"]`);
        if (customizerSizeBtn && !customizerSizeBtn.classList.contains('product-customizer__size-btn--active')) {
          customizerSizeBtn.click();
        }
      });
    });
  }

  syncVariantFromSizeAndColor() {
    const colorVariantsContainer = this.querySelector("[data-color-variants]");
    if (!colorVariantsContainer) return;

    const jsonScript = colorVariantsContainer.querySelector("[data-variants-json]");
    if (!jsonScript) return;

    let variants = [];
    try { variants = JSON.parse(jsonScript.textContent); } catch (e) { return; }

    const activeColorBtn = colorVariantsContainer.querySelector(".mp-color-variants__item--active");
    const currentColor = activeColorBtn ? activeColorBtn.dataset.color : null;

    // Match by size + color (don't filter by show — show only controls color thumbnail visibility)
    const match = variants.find((v) => {
      const sizeMatch = !this.selectedSize || v.option1 === this.selectedSize || v.option2 === this.selectedSize;
      const colorMatch = !currentColor || v.option1 === currentColor || v.option2 === currentColor;
      return sizeMatch && colorMatch;
    });

    if (match) {
      if (this.variantInput) this.variantInput.value = match.id;

      const url = new URL(window.location.href);
      url.searchParams.set("variant", match.id);
      window.history.replaceState({}, "", url.toString());

      const mediasJson = JSON.stringify(match.medias);
      this.updateProductImages(mediasJson);
    }
  }

  initAddToCartBlock() {
    const block = this.querySelector("[data-add-to-cart-block]");
    if (!block) return;

    const btns = block.querySelectorAll("[data-atc-variant-id]");
    const input = block.querySelector("[data-atc-variant-input]");
    const submitBtn = block.querySelector("[data-quick-atc]");
    const priceEl = this.querySelector(".product__price");

    btns.forEach((btn) => {
      btn.addEventListener("click", () => {
        btns.forEach((b) => {
          b.classList.remove("mp-add-to-cart__variant--active");
          b.setAttribute("aria-pressed", "false");
        });
        btn.classList.add("mp-add-to-cart__variant--active");
        btn.setAttribute("aria-pressed", "true");
        if (input) input.value = btn.dataset.atcVariantId;
        if (submitBtn) submitBtn.dataset.variantId = btn.dataset.atcVariantId;
        if (priceEl) priceEl.textContent = btn.dataset.atcVariantPrice;

        const url = new URL(window.location);
        url.searchParams.set("variant", btn.dataset.atcVariantId);
        window.history.replaceState({}, "", url);
      });
    });
  }

  initColorVariants() {
    const colorVariantsContainer = this.querySelector("[data-color-variants]");
    if (!colorVariantsContainer) return;

    this.colorVariantBtns = colorVariantsContainer.querySelectorAll(
      ".mp-color-variants__item",
    );
    this.variantInput = colorVariantsContainer.querySelector(
      "[data-variant-input]",
    );
    this.productSwiper = this.querySelector(".swiper-product");

    // Parse variants JSON for medias lookup
    this.variantsData = [];
    const jsonScript = colorVariantsContainer.querySelector("[data-variants-json]");
    if (jsonScript) {
      try { this.variantsData = JSON.parse(jsonScript.textContent); } catch (e) {}
    }

    // Store original product images for fallback
    this.originalSlidesHtml = this.productSwiper
      ? this.productSwiper.querySelector(".swiper-wrapper").innerHTML
      : "";

    // Apply variant images on load: URL param or default active variant
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get("variant");
    const targetBtn = urlVariantId
      ? colorVariantsContainer.querySelector(
          `[data-variant-id="${urlVariantId}"]`,
        )
      : null;

    if (targetBtn) {
      this.selectColorVariant(targetBtn, false);
    } else {
      const activeBtn = colorVariantsContainer.querySelector(
        ".mp-color-variants__item--active",
      );
      if (activeBtn) {
        const medias = this.getVariantMedias(activeBtn.dataset.variantId);
        this.updateProductImages(JSON.stringify(medias));
      }
    }

    // Bind click events
    this.colorVariantBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        this.selectColorVariant(btn, true);
      });
    });

    // Hide out-of-stock colors using custom stock API
    this.filterColorVariantsByStock(colorVariantsContainer);

    // Listen for color changes from customizer
    document.addEventListener("variant:changed", (e) => {
      if (e.detail.source === "main-product") return;
      const color = e.detail.color;
      if (!color) return;
      const matchingBtn = colorVariantsContainer.querySelector(
        `.mp-color-variants__item[data-color="${color}"]`,
      );
      if (
        matchingBtn &&
        !matchingBtn.classList.contains("mp-color-variants__item--active")
      ) {
        this.selectColorVariant(matchingBtn, true, false);
      }
    });
  }

  getVariantMedias(variantId) {
    const variant = this.variantsData.find((v) => String(v.id) === String(variantId));
    return variant && variant.medias ? variant.medias : [];
  }

  normalizeColorName(name) {
    return (name || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  async filterColorVariantsByStock(container) {
    const productLegacyId = container.dataset.productLegacyId;
    if (!productLegacyId) return;

    const isLocal = ["127.0.0.1", "localhost"].includes(window.location.hostname);
    const apiEndpoint = isLocal
      ? container.dataset.apiEndpointLocal
      : container.dataset.apiEndpoint;
    if (!apiEndpoint) return;

    try {
      const res = await fetch(`${apiEndpoint}/api/stock/public/${productLegacyId}`);
      if (!res.ok) return;
      const data = await res.json();
      if (!data.success) return;
      if (data.noTracking) { this._hasStockData = false; return; }
      if (!Array.isArray(data.stocks)) return;

      // Store full stock data for size-aware filtering
      this._stockData = data.stocks;

      // Build stock map filtered by selected size (if any)
      const stockByColor = {};
      for (const entry of data.stocks) {
        const key = this.normalizeColorName(entry.colorName);
        if (!key) continue;
        // For multi-size products, only count stock for the selected size
        if (this.selectedSize && entry.size) {
          if (this.normalizeColorName(entry.size) !== this.normalizeColorName(this.selectedSize)) continue;
        }
        stockByColor[key] = (stockByColor[key] || 0) + (entry.quantity || 0);
      }

      this._stockByColor = stockByColor;
      this._hasStockData = Object.keys(stockByColor).length > 0;
      this.applyStockState(container);
    } catch (err) {
      console.warn("[MainProduct] Stock fetch failed:", err);
    }
  }

  refilterColorsByStock(container) {
    const stockByColor = {};
    for (const entry of this._stockData) {
      const key = this.normalizeColorName(entry.colorName);
      if (!key) continue;
      if (this.selectedSize && entry.size) {
        if (this.normalizeColorName(entry.size) !== this.normalizeColorName(this.selectedSize)) continue;
      }
      stockByColor[key] = (stockByColor[key] || 0) + (entry.quantity || 0);
    }
    this._stockByColor = stockByColor;
    this._hasStockData = Object.keys(stockByColor).length > 0;
    this.applyStockState(container);
  }

  isColorOos(colorAttr) {
    if (!this._hasStockData) return false;
    const bgColor = (colorAttr || "").split("/")[0];
    const key = this.normalizeColorName(bgColor);
    const qty = this._stockByColor?.[key];
    return qty === undefined || qty <= 0;
  }

  applyStockState(container) {
    this.colorVariantBtns.forEach((btn) => {
      const oos = this.isColorOos(btn.dataset.color);
      btn.style.opacity = oos ? "0.35" : "";
      btn.style.pointerEvents = oos ? "none" : "";
    });

    // If active color is OOS, select first available
    const activeBtn = container.querySelector(".mp-color-variants__item--active");
    if (activeBtn && this.isColorOos(activeBtn.dataset.color)) {
      const firstAvailable = Array.from(this.colorVariantBtns).find(
        (btn) => !this.isColorOos(btn.dataset.color)
      );
      if (firstAvailable) {
        this.selectColorVariant(firstAvailable, true, true);
      }
    }

    // Update button state for current selection
    const currentActive = container.querySelector(".mp-color-variants__item--active");
    this.updateSubmitFromStock(currentActive ? currentActive.dataset.color : null);
  }

  updateSubmitFromStock(colorAttr) {
    if (!this._hasStockData) return;
    const submitBtn = this.querySelector('[type="submit"], .product-form__submit');
    if (!submitBtn) return;
    const oos = !colorAttr || this.isColorOos(colorAttr);
    submitBtn.disabled = oos;
    if (oos) {
      this._originalSubmitText = this._originalSubmitText || submitBtn.textContent;
      submitBtn.textContent = "Rupture de stock";
    } else if (this._originalSubmitText) {
      submitBtn.textContent = this._originalSubmitText;
    }
  }

  selectColorVariant(btn, updateUrl = true, dispatchEvent = true) {
    const variantId = btn.dataset.variantId;
    const variantMedias = JSON.stringify(this.getVariantMedias(variantId));
    const color = btn.dataset.color;

    // Update active state
    this.colorVariantBtns.forEach((b) => {
      b.classList.remove("mp-color-variants__item--active");
      b.setAttribute("aria-pressed", "false");
    });
    btn.classList.add("mp-color-variants__item--active");
    btn.setAttribute("aria-pressed", "true");

    const colorValueEl = this.querySelector("[data-color-value]");
    if (colorValueEl) colorValueEl.textContent = color.split(' / ')[0];

    // Update hidden input
    if (this.variantInput) {
      this.variantInput.value = variantId;
    }

    // Update URL without page reload
    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variantId);
      window.history.replaceState({}, "", url.toString());
    }

    // Update product images with variant medias
    this.updateProductImages(variantMedias);

    // Update add-to-cart button based on stock
    this.updateSubmitFromStock(color);

    // Sync with customizer colors
    if (dispatchEvent && color) {
      document.dispatchEvent(
        new CustomEvent("variant:changed", {
          detail: { variantId, color, source: "main-product" },
        }),
      );
    }
  }

  updateProductImages(variantMediasJson) {
    if (!this.productSwiper) return;

    let medias = [];
    try {
      medias = JSON.parse(variantMediasJson || "[]");
    } catch (e) {
      medias = [];
    }

    const swiperWrapper = this.productSwiper.querySelector(".swiper-wrapper");
    if (!swiperWrapper) return;

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    if (medias.length > 0) {
      swiperWrapper.innerHTML = "";
      medias.forEach((mediaUrl) => {
        const slide = document.createElement("div");
        slide.className = "swiper-slide";
        slide.innerHTML = `
          <div class="slide-img">
            <img src="${mediaUrl}" alt="" loading="lazy">
          </div>
        `;
        swiperWrapper.appendChild(slide);
      });
    } else {
      swiperWrapper.innerHTML = this.originalSlidesHtml || "";
    }

    // Only reinit Swiper on mobile
    if (window.innerWidth < 990) {
      this.swiper = new Swiper(this.productSwiper, {
        slidesPerView: 1.25,
        spaceBetween: 6,
        pagination: {
          el: this.querySelector(".swiper-pagination"),
          clickable: true,
        },
      });
    }
  }

  initImageZoom() {}

  initGalleryModal() {
    const modal = this.querySelector(".mp-gallery-modal");
    if (!modal) return;

    const swiper = this.querySelector(".swiper-product");
    if (swiper) {
      swiper.addEventListener("click", (e) => {
        const slideImg = e.target.closest(".slide-img");
        if (!slideImg) return;
        const index = Array.from(swiper.querySelectorAll(".slide-img")).indexOf(slideImg);
        this.openGalleryModal(index);
      });
    }

    const closeBtn = modal.querySelector(".mp-gallery-modal__close");
    closeBtn?.addEventListener("click", () => this.closeGalleryModal());

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !modal.hasAttribute("hidden")) {
        this.closeGalleryModal();
      }
    });
  }

  openGalleryModal(startIndex = 0) {
    const modal = this.querySelector(".mp-gallery-modal");
    if (!modal) return;

    const scrollEl = modal.querySelector(".mp-gallery-modal__scroll");
    if (!scrollEl) return;

    const slides = this.querySelectorAll(".swiper-product .slide-img img");
    scrollEl.innerHTML = "";
    slides.forEach((img) => {
      const el = document.createElement("img");
      const src = img.currentSrc || img.src;
      // Shown larger than in the gallery: let the browser fetch a wider file
      // only when the screen needs it (sizes mirrors main-product.css).
      if (/[?&]width=\d+/.test(src)) {
        const atWidth = (w) => src.replace(/([?&]width=)\d+/, `$1${w}`);
        el.srcset = [832, 1200, 2000].map((w) => `${atWidth(w)} ${w}w`).join(", ");
        el.sizes = "(min-width: 990px) 72vh, 100vw";
      }
      el.src = src;
      el.alt = img.alt || "";
      el.className = "mp-gallery-modal__image";
      scrollEl.appendChild(el);
    });

    modal.removeAttribute("hidden");
    document.body.style.overflow = "hidden";

    // Open on the photo that was clicked. Heights come from the CSS ratio, so
    // the offset is right before the images have loaded.
    const target = scrollEl.children[startIndex];
    scrollEl.scrollTop = target ? target.offsetTop - scrollEl.offsetTop : 0;
  }

  closeGalleryModal() {
    const modal = this.querySelector(".mp-gallery-modal");
    if (!modal) return;
    modal.setAttribute("hidden", "");
    document.body.style.overflow = "";
  }

  async quickAddToCart(variantId, quantity = 1) {
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }),
      });

      if (!res.ok) {
        console.error("ADD ERROR", res.status, await res.text());
        return;
      }

      await res.json();

      const cartRes = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
        cache: "no-store",
      });
      const cart = await cartRes.json();

      document.dispatchEvent(
        new CustomEvent("cart:updated", { detail: { cart } }),
      );
      document.querySelector("side-cart")?.open?.();
    } catch (e) {
      console.error(e);
    }
  }
}

customElements.define("main-product", MainProduct);
